import { Board } from '@puzzlio/core';

// Edge bitmask directions (bits 0-3)
export const EDGE_UP = 1;
export const EDGE_RIGHT = 2;
export const EDGE_DOWN = 4;
export const EDGE_LEFT = 8;
export const NOTE_X = 16; // legacy whole-cell note — kept for compat

// Directional edge notes (bits 5-8): mark a specific edge as "don't go here"
export const NOTE_EDGE_UP = 32;
export const NOTE_EDGE_RIGHT = 64;
export const NOTE_EDGE_DOWN = 128;
export const NOTE_EDGE_LEFT = 256;
export const ALL_EDGE_NOTES = NOTE_EDGE_UP | NOTE_EDGE_RIGHT | NOTE_EDGE_DOWN | NOTE_EDGE_LEFT;

// Cell types in the grid definition
export const BLACK_CELL = 0;
export const WHITE_CELL = 1;

export type GridCell = typeof BLACK_CELL | typeof WHITE_CELL;
export type Grid = GridCell[][];

// Cell types in the walls definition
export const WALL_OFF = 0;
export const WALL_ON = 1;
export type WallCell = typeof WALL_OFF | typeof WALL_ON;
// We track only if there is a wall to the right of a cell, and to the bottom
export type Walls = { h: WallCell[][]; v: WallCell[][] };

// Solution is a bitmask per cell showing correct edge connections
export type SolutionGrid = number[][];

// Play cells — bitmask encoding edges (bits 0-3) and X note (bit 4)
export type CellValue = number;

export const LOOPY_RIVER_GAME_ID = 4;

export interface LoopyRiverBoard extends Board {
  grid: Grid;
  walls: Walls;
  solution: SolutionGrid;
}

export const hasEdge = (cell: CellValue, direction: number): boolean => (cell & direction) !== 0;
export const hasNote = (cell: CellValue): boolean => (cell & NOTE_X) !== 0;
export const hasEdgeNote = (cell: CellValue, direction: number): boolean => {
  // Convert edge direction to note direction (shift left by 5)
  const noteDir = direction << 5;
  return (cell & noteDir) !== 0;
};
export const hasAnyEdgeNote = (cell: CellValue): boolean => (cell & ALL_EDGE_NOTES) !== 0;
export const getEdges = (cell: CellValue): number => cell & 0xf;
export const getEdgeNotes = (cell: CellValue): number => (cell >> 5) & 0xf;
export const isBlackCell = (gridCell: GridCell): boolean => gridCell === BLACK_CELL;

/** Returns true if there is a wall on the shared edge between two adjacent cells. */
export const hasWallBetween = (
  walls: Walls,
  fromRow: number,
  fromCol: number,
  toRow: number,
  toCol: number
): boolean => {
  const dr = toRow - fromRow;
  const dc = toCol - fromCol;
  if (dc === 1) return walls.v[fromRow]?.[fromCol] === WALL_ON; // moving RIGHT: v wall on right edge of fromCol
  if (dc === -1) return walls.v[fromRow]?.[toCol] === WALL_ON; // moving LEFT:  v wall on right edge of toCol
  if (dr === 1) return walls.h[fromRow]?.[fromCol] === WALL_ON; // moving DOWN:  h wall on bottom edge of fromRow
  if (dr === -1) return walls.h[toRow]?.[fromCol] === WALL_ON; // moving UP:    h wall on bottom edge of toRow
  return false;
};

// Convert edge direction constant to its note equivalent
export const edgeToNote = (dir: number): number => {
  switch (dir) {
    case EDGE_UP:
      return NOTE_EDGE_UP;
    case EDGE_RIGHT:
      return NOTE_EDGE_RIGHT;
    case EDGE_DOWN:
      return NOTE_EDGE_DOWN;
    case EDGE_LEFT:
      return NOTE_EDGE_LEFT;
    default:
      return 0;
  }
};

// Opposite direction mapping
export const oppositeDirection = (dir: number): number => {
  switch (dir) {
    case EDGE_UP:
      return EDGE_DOWN;
    case EDGE_DOWN:
      return EDGE_UP;
    case EDGE_LEFT:
      return EDGE_RIGHT;
    case EDGE_RIGHT:
      return EDGE_LEFT;
    default:
      return 0;
  }
};

// Direction to row/col delta
export const directionDelta = (dir: number): [number, number] => {
  switch (dir) {
    case EDGE_UP:
      return [-1, 0];
    case EDGE_DOWN:
      return [1, 0];
    case EDGE_LEFT:
      return [0, -1];
    case EDGE_RIGHT:
      return [0, 1];
    default:
      return [0, 0];
  }
};

/** A board message attribute: show the checkerboard while it's up, for a hint that argues from the cells' colours. */
export const CHECKERBOARD_ATTR = 'checkerboard';

/**
 * How a hint names the side of its cell it's about, in a mark's `markData`: a hint is about the lines
 * between cells, and a mark can only name a cell.
 */
export type EdgeName = 'up' | 'right' | 'down' | 'left';

export const EDGE_BY_NAME: Record<EdgeName, number> = {
  up: EDGE_UP,
  right: EDGE_RIGHT,
  down: EDGE_DOWN,
  left: EDGE_LEFT,
};

export const edgeName = (dir: number): EdgeName =>
  dir === EDGE_UP ? 'up' : dir === EDGE_RIGHT ? 'right' : dir === EDGE_DOWN ? 'down' : 'left';

// Direction from one cell to an adjacent cell
export const directionBetween = (fromRow: number, fromCol: number, toRow: number, toCol: number): number | null => {
  const dr = toRow - fromRow;
  const dc = toCol - fromCol;
  if (dr === -1 && dc === 0) return EDGE_UP;
  if (dr === 1 && dc === 0) return EDGE_DOWN;
  if (dr === 0 && dc === -1) return EDGE_LEFT;
  if (dr === 0 && dc === 1) return EDGE_RIGHT;
  return null;
};
